import type { Resource } from '__types__';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearContactDuplicates } from 'store/v1/contacts/contacts.actions.js';
import {
  getContactDetailPath,
  type WorkspaceRef
} from 'v5/core/app-scope/entityWorkspaceRouting';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithGlobalSettingsService } from 'v5/services/internal/GlobalSettingsService/GlobalSettingsService';
import { ResourceDuplicatesFooter } from './ResourceDuplicatesFooter';

export interface ResourceDuplicatesFooterConnectedProps
  extends WithServices<[WithGlobalSettingsService]> {
  typedName: string;
  organizationId: string;
  browsingWorkspaceId: number | null;
  workspaces: WorkspaceRef[] | undefined;
}

/**
 * Reads the legacy duplicate check result (`contacts.duplicates`, filled by
 * `checkContactDuplicates` while the name is typed). Dismissing hides the
 * current set of matches only; a different set shows up again.
 */
export function ResourceDuplicatesFooterConnected({
  typedName,
  organizationId,
  browsingWorkspaceId,
  workspaces,
  services
}: ResourceDuplicatesFooterConnectedProps) {
  const dispatch = useDispatch();
  const duplicates = useSelector(
    (state: { contacts: { duplicates: unknown } }) => state.contacts.duplicates
  );
  const matches = Array.isArray(duplicates) ? (duplicates as Resource[]) : [];
  const signature = matches.map(match => match.id).join(',');
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(
    null
  );

  // Stale matches must not greet the next entry (reset, reopen).
  useEffect(
    () => () => {
      dispatch(clearContactDuplicates());
    },
    [dispatch]
  );

  if (
    matches.length === 0 ||
    typedName.trim() === '' ||
    signature === dismissedSignature
  ) {
    return null;
  }

  return (
    <ResourceDuplicatesFooter
      matches={matches}
      typedName={typedName}
      getProfileHref={match =>
        getContactDetailPath(
          organizationId,
          { enterprise_workspace_id: browsingWorkspaceId },
          workspaces,
          match.id
        )
      }
      onDismiss={() => setDismissedSignature(signature)}
      services={services}
    />
  );
}
